<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Specialities</h4>
      <el-button type="primary" size="small" @click="showCreateDialog">Create</el-button>
    </div>

    <card>
      <el-input v-model="search" placeholder="Search specialities..." clearable class="mb-3" style="max-width:400px" />

      <el-table :data="filteredList" v-loading="loading" style="width: 100%">
        <el-table-column label="ID" prop="id" width="250" />
        <el-table-column label="Name EN" min-width="200">
          <template slot-scope="{ row }">
            {{ row.name && row.name.en || '' }}
          </template>
        </el-table-column>
        <el-table-column label="Name RU" min-width="200">
          <template slot-scope="{ row }">
            {{ row.name && row.name.ru || '' }}
          </template>
        </el-table-column>
        <el-table-column fixed="right" width="80">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="editItem(row)">Edit</el-button>
          </template>
        </el-table-column>
      </el-table>
    </card>

    <!-- Create/Edit Dialog -->
    <el-dialog :title="dialogMode === 'create' ? 'Create Speciality' : 'Edit Speciality'" :visible.sync="dialogVisible" width="600px">
      <div class="mb-3">
        <label>ID *</label>
        <el-input v-model="form.id" placeholder="e.g. cardiologist" :disabled="dialogMode === 'edit'" />
      </div>
      <div class="mb-3">
        <label>Illness Kind ID</label>
        <el-select v-model="form.illness_kind_id" filterable clearable placeholder="Select illness kind" style="width: 100%">
          <el-option v-for="ik in illnessKinds" :key="ik.id" :label="ik.name && (ik.name.en || ik.name.ru) || ik.id" :value="ik.id" />
        </el-select>
      </div>
      <el-tabs v-model="activeLang">
        <el-tab-pane v-for="lang in langs" :key="lang" :label="lang.toUpperCase()" :name="lang">
          <el-input v-model="form.name[lang]" :placeholder="`Name in ${lang.toUpperCase()}`" />
        </el-tab-pane>
      </el-tabs>
      <span slot="footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="saving" @click="saveItem">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

function emptyLangs(langs = []) {
  return langs.reduce((acc, l) => { acc[l] = ""; return acc; }, {});
}

export default {
  data() {
    return {
      loading: false,
      saving: false,
      search: "",
      activeLang: "en",
      dialogVisible: false,
      dialogMode: "create",
      form: {
        id: "",
        name: {},
        illness_kind_id: null,
      },
    };
  },
  computed: {
    ...mapState("specialities", ["specialities"]),
    ...mapState("cmsApiCatalogs", { illnessKinds: "illness-kinds", domains: "domains" }),
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
    filteredList() {
      if (!this.search) return this.specialities;
      const q = this.search.toLowerCase();
      return this.specialities.filter(s => {
        const nameEn = (s.name && s.name.en || "").toLowerCase();
        const nameRu = (s.name && s.name.ru || "").toLowerCase();
        const id = (s.id || "").toLowerCase();
        return nameEn.includes(q) || nameRu.includes(q) || id.includes(q);
      });
    },
  },
  methods: {
    ...mapActions("specialities", ["getSpecialities", "upsertSpeciality"]),
    ...mapActions("cmsApiCatalogs", ["getIllnessKinds", "getDomains"]),
    resetForm() {
      this.form = { id: "", name: emptyLangs(this.langs), illness_kind_id: null };
      this.activeLang = this.langs[0] || "en";
    },
    showCreateDialog() {
      this.dialogMode = "create";
      this.resetForm();
      this.dialogVisible = true;
    },
    editItem(row) {
      this.dialogMode = "edit";
      this.form = {
        id: row.id,
        name: { ...emptyLangs(this.langs), ...(row.name || {}) },
        illness_kind_id: row.illness_kind_id || null,
      };
      this.activeLang = this.langs[0] || "en";
      this.dialogVisible = true;
    },
    async saveItem() {
      if (!this.form.id) {
        return this.$notify({ type: "warning", title: "ID is required" });
      }
      if (!this.form.name.en) {
        return this.$notify({ type: "warning", title: "Name EN is required" });
      }
      this.saving = true;
      try {
        await this.upsertSpeciality({
          id: this.form.id,
          name: this.form.name,
          illness_kind_id: this.form.illness_kind_id || 0,
        });
        this.$notify({ type: "success", title: "Speciality saved" });
        this.dialogVisible = false;
        await this.getSpecialities();
      } catch (e) {
        this.$notify({ type: "warning", title: "Error saving speciality" });
      } finally {
        this.saving = false;
      }
    },
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.getDomains(),
      this.getSpecialities(),
      this.getIllnessKinds({ perPage: 1500 }),
    ]).finally(() => { this.loading = false; });
    this.resetForm();
  },
};
</script>
